import React from "react";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import scrollHandler from "../../utils/scrollHandler";
import { useNavigate, useParams } from "react-router-dom";
import { Button, IconButton, InputAdornment, Pagination } from "@mui/material";
import { ArrowBack, ContentCopy, InfoOutlined, PendingActionsOutlined, Search as SearchIcon } from "@mui/icons-material";
import MoneyIcon from "../../lib/assets/icons/referral__icon-2.svg";
import MoneyIconBlue from "../../lib/assets/icons/referral__icon-5.svg";
import MoneyCashIcon from "../../lib/assets/icons/referral__icon-1.svg";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";
import referralsService from "../../service/referralsService";
import walletService from "../../service/walletService";
import PreloaderTable from "../../lib/components/PreloaderTable";
import RemDataTable from "./components/rem-data-table";
import InitiatePayoutModal from "./components/initiate-payout-modal";
import AppTextField from "../../lib/components/AppTextField";
import AppSelectField from "../../lib/components/AppSelectField";
import { moneyFormat } from "../../utils/moneyFormat";
import { toast } from "react-toastify";
import AppTab from "../../components/tab-label";
import AppExportButton from "../../lib/components/AppExportButton";
import StatsCard from "./components/stats-card";

const STATUS_TABS = [
  { label: "All", value: "all" },
  { label: "Completed", value: "completed" },
  { label: "Processing", value: "processing" },
  { label: "Failed", value: "failed" },
];

const ViewPayouts = () => {
  const navigate = useNavigate();
  const { id: referrerId } = useParams();
  const [payoutModalOpen, setPayoutModalOpen] = React.useState(false);
  const [isCopied, setIsCopied] = React.useState({ link: false, code: false });
  const searchParams = new URLSearchParams(window.location.search);
  const page = searchParams.get("page") || "1";
  const limit = searchParams.get("limit") || "20";
  const status = searchParams.get("status") || "";
  const keyword = searchParams.get("keyword") || "";

  const query = { page, limit, status };

  // 1. Fetch Referrer Profile details
  const referrerQuery = useQuery({
    retry: false,
    queryKey: ["referrer", referrerId],
    queryFn: () => referralsService.getReferrer({ id: referrerId }),
  });

  const referrer = referrerQuery?.data?.data?.data;
  const userId = referrer?.accountMeta?._id || referrer?.accountMeta?.id || (typeof referrer?.account === "string" ? referrer.account : referrer?.account?._id || referrer?.account?.id) || referrer?.accountId || referrer?.userId || referrerId;

  // 2. Fetch Overall Payouts Stats (unfiltered to ensure summary cards & tab counts stay stable across tabs)
  const overallPayoutsQuery = useQuery({
    retry: false,
    queryKey: ["userPayoutsOverall", userId],
    queryFn: () => walletService.getPayouts(userId, { limit: 100 }),
    enabled: Boolean(userId),
  });

  // 3. Fetch Payouts Ledger for active page/tab
  const payoutsQuery = useQuery({
    retry: false,
    queryKey: ["userPayouts", userId, ...Object.values(query)],
    queryFn: () => walletService.getPayouts(userId, query),
    enabled: Boolean(userId),
  });

  // 4. Fetch Referrer Wallet / Available Balance
  const walletQuery = useQuery({
    retry: false,
    queryKey: ["wallet", userId],
    queryFn: () => walletService.getWallet(userId),
    enabled: Boolean(userId),
  });

  const availableBalance = walletQuery.isSuccess && walletQuery.data?.data?.data?.balance !== undefined
    ? walletQuery.data.data.data.balance
    : walletQuery.isSuccess && typeof walletQuery.data?.data?.balance === "number"
      ? walletQuery.data.data.balance
      : 0;

  const payoutsData = payoutsQuery?.data?.data;
  const isSummaryLoading = !overallPayoutsQuery.isSuccess && !payoutsQuery.isSuccess;
  const overallData = overallPayoutsQuery?.data?.data || payoutsData;

  const rawSummary = overallData?.summary || payoutsData?.summary;
  const rawTabCounts = overallData?.tabCounts || payoutsData?.tabCounts;

  const summary = React.useMemo(() => {
    if (rawSummary && typeof rawSummary.totalAmount === "number") {
      return rawSummary;
    }
    const docs = overallData?.docs || overallData?.data || payoutsData?.docs || payoutsData?.data || [];
    const totalAmount = docs.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
    const successfulDocs = docs.filter(d => String(d.status).toLowerCase() === "completed" || String(d.status).toLowerCase() === "successful");
    const successfulAmount = successfulDocs.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
    const failedOrPendingDocs = docs.filter(d => String(d.status).toLowerCase() !== "completed" && String(d.status).toLowerCase() !== "successful");
    const failedOrPendingAmount = failedOrPendingDocs.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);

    return {
      total: docs.length,
      successful: successfulDocs.length,
      failedOrPending: failedOrPendingDocs.length,
      totalAmount,
      successfulAmount,
      failedOrPendingAmount
    };
  }, [rawSummary, overallData?.docs, overallData?.data, payoutsData?.docs, payoutsData?.data]);

  const tabCounts = React.useMemo(() => {
    if (rawTabCounts && typeof rawTabCounts.all === "number") {
      return rawTabCounts;
    }
    const docs = overallData?.docs || overallData?.data || payoutsData?.docs || payoutsData?.data || [];
    const completed = docs.filter(d => String(d.status).toLowerCase() === "completed" || String(d.status).toLowerCase() === "successful").length;
    const processing = docs.filter(d => String(d.status).toLowerCase() === "processing" || String(d.status).toLowerCase() === "pending").length;
    const failed = docs.filter(d => String(d.status).toLowerCase() === "failed" || String(d.status).toLowerCase() === "reversed").length;

    return {
      all: docs.length,
      completed,
      processing,
      failed
    };
  }, [rawTabCounts, overallData?.docs, overallData?.data, payoutsData?.docs, payoutsData?.data]);

  const copyToClipboard = (elementId, type) => {
    const text = document.getElementById(elementId)?.innerText;
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setIsCopied((prev) => ({ ...prev, [type]: true }));
      toast.success(`${type === "link" ? "Referral link" : "Referral code"} copied to clipboard!`);
      setTimeout(() => {
        setIsCopied((prev) => ({ ...prev, [type]: false }));
      }, 2000);
    });
  };

  // Local filtering by search keyword
  const filteredDocs = React.useMemo(() => {
    const docs = payoutsData?.docs || payoutsData?.data || [];
    if (!keyword) return docs;
    const cleanKw = keyword.toLowerCase();
    return docs.filter(doc => 
      (doc.transactionId && doc.transactionId.toLowerCase().includes(cleanKw)) ||
      (doc.accountPaidTo?.bankName && doc.accountPaidTo.bankName.toLowerCase().includes(cleanKw)) ||
      (doc.accountPaidTo?.accountNumber && doc.accountPaidTo.accountNumber.includes(cleanKw)) ||
      (doc.status && doc.status.toLowerCase().includes(cleanKw))
    );
  }, [payoutsData?.docs, payoutsData?.data, keyword]);

  const [isExporting, setIsExporting] = React.useState(false);

  // CSV export of ledger data aligned with backend spec
  const exportToCSV = async () => {
    if (!userId) {
      toast.warning("User details not loaded yet.");
      return;
    }
    setIsExporting(true);
    try {
      await walletService.exportPayouts(userId, query);
      toast.success("Payouts ledger exported successfully!");
    } catch (e) {
      // Fallback export if backend export endpoint is unavailable
      const docs = filteredDocs;
      if (!docs.length) {
        toast.warning("No payouts transactions to export.");
        return;
      }
      const headers = ["Date", "Transaction ID", "Account Name", "Account Number", "Bank Code", "Amount (NGN)", "Status"];
      const rows = docs.map((doc) => [
        moment(doc.date || doc.createdAt).format("YYYY-MM-DD HH:mm"),
        doc.transactionId || doc._id || "N/A",
        doc.accountPaidTo?.accountName || doc.accountName || "N/A",
        doc.accountPaidTo?.accountNumber || "N/A",
        doc.accountPaidTo?.bankCode || "N/A",
        doc.amount,
        doc.status,
      ]);

      const csvContent =
        "data:text/csv;charset=utf-8," +
        [headers.join(","), ...rows.map((e) => e.map((val) => `"${String(val ?? '').replace(/"/g, '""')}"`).join(","))].join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `payouts_${moment().format("YYYYMMDD")}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.info("Exported current ledger view.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleSearchChange = (value) => {
    searchParams.set("keyword", value);
    searchParams.set("page", "1");
    navigate(
      {
        pathname: window.location.pathname,
        search: searchParams.toString(),
      },
      { replace: true }
    );
  };

  const handleStatusChange = (value) => {
    const statusVal = !value || value.toUpperCase() === "ALL" ? "" : value.toLowerCase();
    searchParams.set("status", statusVal);
    searchParams.set("page", "1");
    navigate(
      {
        pathname: window.location.pathname,
        search: searchParams.toString(),
      },
      { replace: true }
    );
  };

  const handlePageChange = (value) => {
    searchParams.set("page", value);
    navigate(
      {
        pathname: window.location.pathname,
        search: searchParams.toString(),
      },
      { replace: true },
      { scrollY: 0 }
    );
    scrollHandler();
  };

  const handleLimitChange = (e) => {
    searchParams.set("limit", e.target.value);
    searchParams.set("page", "1");
    navigate(
      {
        pathname: window.location.pathname,
        search: searchParams.toString(),
      },
      { replace: true }
    );
  };

  return (
    <AppLayout>
      <Header
        title="Payout"
        description="Get insights to Referrers that have been paid from this page"
      />
      <div className="p-3 bg-light" style={{ minHeight: "100%" }}>
        <IconButton
          className="bg-secondary-subtle mb-3"
          onClick={() => navigate(-1)}
        >
          <ArrowBack />
        </IconButton>

        {/* Profile Card & Action Buttons */}
        <div className="d-flex flex-wrap align-items-end mb-5">
          <div className="bg-white rounded border" style={{ maxWidth: 480, width: "100%" }}>
            {referrerQuery.isLoading ? (
              <div className="p-4 text-center">Loading Profile...</div>
            ) : (
              <div className="p-3">
                <div className="d-flex gap-4 mb-2">
                  <div className="flex-grow-1 fw-medium fs-5">
                    {referrer?.accountMeta?.firstName} {referrer?.accountMeta?.lastName}
                  </div>
                </div>
                <div className="mb-2 fs-sm">
                  <span className="text-secondary me-2">Email:</span>
                  <span className="fw-medium">{referrer?.accountMeta?.email}</span>
                </div>
                <div className="mb-2 fs-sm">
                  <span className="text-secondary me-2">Phone Number:</span>
                  <span className="fw-medium">
                    {referrer?.accountMeta?.phone?.startsWith("+") ? (
                      <span>{referrer?.accountMeta?.phone}</span>
                    ) : (
                      <span className={referrer?.accountMeta?.phone ? "" : "d-none"}>
                        +{referrer?.accountMeta?.phone}
                      </span>
                    )}
                  </span>
                </div>
                <div className="mb-2 fs-sm">
                  <span className="text-secondary me-2">Invited Businesses:</span>
                  <span className="fw-medium">{referrer?.count?.referral || 0}</span>
                </div>
                <div className="mb-2 fs-sm">
                  <span className="text-secondary me-2">Paid Invitees:</span>
                  <span className="fw-medium">{referrer?.count?.paid || 0}</span>
                </div>
                <div className="mb-2 d-flex w-100 align-items-center">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center">
                      <div className="text-secondary me-2 text-nowrap fs-sm">Referral Link:</div>
                      <div>
                        <a
                          rel="noreferrer"
                          target="_blank"
                          id="referral-link"
                          className="fw-medium fs-sm text-truncate-1 text-decoration-underline"
                          href={`https://app.jureb.com?ref=${referrer?.code}`}
                        >
                          https://app.jureb.com?ref={referrer?.code}
                        </a>
                      </div>
                    </div>
                  </div>
                  <Button
                    size="small"
                    endIcon={<ContentCopy />}
                    onClick={() => copyToClipboard("referral-link", "link")}
                  >
                    {isCopied.link ? "Copied" : "Copy"}
                  </Button>
                </div>
                <div className="mb-2 d-flex align-items-center">
                  <div className="fs-sm flex-grow-1">
                    <span className="text-secondary me-2">Referral Code:</span>
                    <span className="fw-medium">
                      <span id="referral-code">{referrer?.code}</span>
                    </span>
                  </div>
                  <Button
                    size="small"
                    endIcon={<ContentCopy />}
                    onClick={() => copyToClipboard("referral-code", "code")}
                  >
                    {isCopied.code ? "Copied" : "Copy"}
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="flex-grow-1 gap-4 d-flex justify-content-end mt-3 mt-md-0">
            <AppExportButton onClick={exportToCSV} disabled={!userId} isLoading={isExporting} />
            <Button
              onClick={() => setPayoutModalOpen(true)}
              disabled={!userId}
              variant="contained"
              color="secondary"
            >
              Initiate Payout
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="row">
          <StatsCard
            className="col-12 col-sm-6 col-xl-3"
            title="Total Payouts"
            amount={moneyFormat(summary?.totalAmount || 0, "NGN")}
            icon={MoneyCashIcon}
            isLoading={isSummaryLoading}
          />
          <StatsCard
            className="col-12 col-sm-6 col-xl-3"
            title="Successful Payouts"
            amount={moneyFormat(summary?.successfulAmount || 0, "NGN")}
            subtitle={`${summary?.successful || 0} Completed Transactions`}
            icon={MoneyIcon}
            isLoading={isSummaryLoading}
          />
          <StatsCard
            className="col-12 col-sm-6 col-xl-3"
            title="Failed/Pending"
            amount={moneyFormat(summary?.failedOrPendingAmount || 0, "NGN")}
            subtitle={
              summary?.failedOrPending > 0 ? (
                <span className="d-inline-flex align-items-center gap-1 text-danger">
                  <InfoOutlined sx={{ fontSize: 14 }} />
                  {summary.failedOrPending} {summary.failedOrPending === 1 ? "action required" : "actions required"}
                </span>
              ) : (
                "0 Actions Required"
              )
            }
            icon={PendingIcon}
            isLoading={isSummaryLoading}
          />
          <StatsCard
            className="col-12 col-sm-6 col-xl-3"
            title="Available balance"
            amount={moneyFormat(availableBalance, "NGN")}
            icon={MoneyIconBlue}
            isLoading={!walletQuery.isSuccess}
          />
        </div>

        {/* Search & Status Filters */}
        <div className="row mb-3">
          <div className="col-6 col-md-3 col-lg-4 mb-3">
            <AppTextField
              placeholder="Search transactions..."
              value={keyword}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon className="text-secondary" />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div className="col-6 col-md-3 col-lg-2 mb-3">
            <AppSelectField
              value={status ? status.toLowerCase() : "all"}
              placeholder="Status"
              onChange={(e) => handleStatusChange(e.target.value)}
              items={[
                { label: "All", value: "all" },
                { label: "Completed", value: "completed" },
                { label: "Processing", value: "processing" },
                { label: "Failed", value: "failed" },
              ]}
            />
          </div>
        </div>

        <div className="mb-3">
          <AppTab
            items={STATUS_TABS}
            value={status ? status.toLowerCase() : "all"}
            onChange={(_, value) => handleStatusChange(value)}
            statsMap={{
              all: tabCounts?.all || 0,
              completed: tabCounts?.completed || 0,
              processing: tabCounts?.processing || 0,
              failed: tabCounts?.failed || 0,
            }}
          />
        </div>

        {/* Transaction History Table */}
        <PreloaderTable isLoading={payoutsQuery.isLoading} />
        {!userId ? (
          <div className="p-4 text-center text-muted">Loading Payouts ledger...</div>
        ) : (
          <>
            <RemDataTable
              isLoading={payoutsQuery.isLoading}
              data={filteredDocs}
              page={Number(page)}
              pageSize={Number(limit)}
            />
            <div className="my-3">
              <div className="d-flex align-items-center gap-4 justify-content-between">
                <div>
                  <AppSelectField
                    value={limit}
                    placeholder="Limit"
                    onChange={handleLimitChange}
                    items={[
                      { label: "20", value: "20" },
                      { label: "30", value: "30" },
                      { label: "40", value: "40" },
                      { label: "50", value: "50" },
                    ]}
                  />
                </div>
                <div>
                  <Pagination
                    page={Number(page)}
                    count={payoutsData?.totalPages || 0}
                    onChange={(_, value) => {
                      handlePageChange(value);
                    }}
                    shape="rounded"
                    color="primary"
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <InitiatePayoutModal
        open={payoutModalOpen}
        onClose={() => setPayoutModalOpen(false)}
        referrer={referrer}
        userId={userId}
        refetch={() => {
          payoutsQuery.refetch();
          overallPayoutsQuery.refetch();
          walletQuery.refetch();
        }}
      />
    </AppLayout>
  );
};

// Matches the tinted-circle style of the referral__icon images used on the other cards
const PendingIcon = (
  <div
    className="d-flex align-items-center justify-content-center rounded-circle w-100 h-100"
    style={{ backgroundColor: "#FEF3C7", color: "#D97706" }}
  >
    <PendingActionsOutlined />
  </div>
);

export default ViewPayouts;
